// @flow

import * as React from "react";
import axios from 'axios';

import spinner from "../images/spinner.gif"
import qb_connect_button from "../images/qb_connect_button.svg"
import FrozenSubscriptionBanner from "./FrozenSubscriptionBanner.react";
import SimpleModal from "./SimpleModal.react";
import LoadingScreen from "./LoadingScreen.react";
import PdfLoadingSpinner from "./PdfLoadingSpinner.react";
import {Document, Page as PDFPage} from "react-pdf";
import {isValidEmailAddress, isQuickBooksConnected, showGenericErrorToast, SAFE_PDF_OPTIONS, isPremiumRequired, emailSendErrorMessage} from "../Utils"
import {toast} from "react-toastify";
import {FROZEN_MESSAGE, EMAIL_PREMIUM_TITLE, EMAIL_PREMIUM_MESSAGE, QUOTA_PREMIUM_TITLE, QUOTA_PREMIUM_MESSAGE_FREE,
        QUOTA_PREMIUM_MESSAGE_PAID} from "../Constants";
import PremiumFeatureModal from "./PremiumFeatureModal.react";
import {GridIcon, DownloadIcon, EyeIcon, SendIcon} from "./Icons.react";


const QR_TYPE_LABELS = {"1": "Link QR", "2": "Data QR", "3": "FATOORA e-Invoice", "4": "Payment Link QR"};

// The server refuses every action on QR invoices while a failed payment has the company frozen (403 company_inactive).
// The buttons are already disabled then; this covers a tab that was opened before the payment failed.
function isFrozenError(err) {
    return !!(err && err.response && err.response.status === 403 && err.response.data && err.response.data.error === "company_inactive");
}

class AllInvoices extends React.Component {

    constructor() {
        super();
        this.state = {
            isLoading: true,
            invoices: [],
            totalEntries: 0,
            totalPages: 1,
            currentPage: 1,
            viewQrInvoiceModalVisibility: false,
            selectedInvoiceToViewTitle: '',
            selectedInvoiceToViewUrl: '',
            sendQrInvoiceModalVisibility: false,
            selectedInvoiceToSendTitle: '',
            selectedInvoiceToSendUrl: '',
            selectedInvoiceToSendId: '',
            nowDownloadingQrInvoiceId: '',
            userSettings: {},
            messageRecipient: '',
            messageRecipientIsInvalid: false,
            messageSubject: '',
            messageSubjectIsInvalid: false,
            messageContent: '',
            sendInvoiceButtonLoading: false,
            searchQuery: '',
            // Emailing a QR invoice is a paid-plan feature: a Free company gets the plans instead ({id, title}).
            premiumEmailInvoice: null,
            // The company has no QR invoices left this period: "New QR Invoice" offers bigger plans instead.
            premiumQuota: false,
        };

        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
        this.downloadInvoice = this.downloadInvoice.bind(this);
        this.viewInvoice = this.viewInvoice.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.sendEmail = this.sendEmail.bind(this);
    }

    componentDidMount() {
        this.loadInvoices(1);
        axios.get(window.SERVER_URL + '/get_settings').then((res) => {
            this.setState({userSettings: res.data.result});
        });
    }

    loadInvoices(page) {
        axios.get(window.SERVER_URL + '/get_qr_invoices?index=' + page).then((response) => {
            this.setState({
                isLoading: false,
                invoices: response.data.result.qr_invoices,
                totalEntries: response.data.result.total_entries,
                totalPages: response.data.result.total_pages,
                currentPage: page,
            });
        });
    }

    previousPage() {
        if (this.state.currentPage > 1) {
            this.loadInvoices(this.state.currentPage - 1);
        }
    }

    nextPage() {
        this.loadInvoices(this.state.currentPage + 1);
    }

    downloadInvoice(invoiceId, invoiceTitle) {
        this.setState({nowDownloadingQrInvoiceId: invoiceId});
        axios.get(window.SERVER_URL + '/download_user_qr_pdf/' + invoiceId + '.pdf').then((res) => {
            const link = document.createElement('a');
            link.href = res.data;
            link.setAttribute('target', '_blank');
            link.setAttribute('download', invoiceTitle + " (Scan2Pay).pdf");
            document.body.appendChild(link);
            link.click();
            link.remove();
            this.setState({nowDownloadingQrInvoiceId: ''});
        }).catch((err) => {
            if (isFrozenError(err)) {
                toast.error(FROZEN_MESSAGE);
            } else {
                showGenericErrorToast();
            }
            this.setState({nowDownloadingQrInvoiceId: ''});
        });
    }

    viewInvoice(invoiceId, invoiceTitle) {
        this.setState({
            selectedInvoiceToViewTitle: invoiceTitle,
            selectedInvoiceToViewUrl: window.SERVER_URL + '/mirror_user_qr_pdf/' + invoiceId + '.pdf',
            viewQrInvoiceModalVisibility: true
        });
    }

    handleChange(e) {
        let value = e.target.value;
        if ((e.target.name === "messageSubject" || e.target.name === "messageRecipient") && value.length > 200) {
            return;
        }

        this.setState({[e.target.name]: value});
    }

    sendInvoice(invoiceId, invoiceTitle) {
        if ((this.state.userSettings.plan_id || 1) <= 1) {
            this.setState({premiumEmailInvoice: {id: invoiceId, title: invoiceTitle}});
            return;
        }
        this.openSendForm(invoiceId, invoiceTitle);
    }

    openSendForm(invoiceId, invoiceTitle) {
        this.setState({
            selectedInvoiceToSendId: invoiceId,
            selectedInvoiceToSendTitle: invoiceTitle,
            selectedInvoiceToSendUrl: window.SERVER_URL + '/mirror_user_qr_pdf/' + invoiceId + '.pdf',
            sendQrInvoiceModalVisibility: true,
            messageSubject: invoiceTitle.substring(0, invoiceTitle.indexOf('(') - 1) + " from " + this.state.userSettings.company_name,
            messageContent: "Hello,\n\nWe appreciate your business. Please find your invoice details here. Feel free to contact us if you have any questions.\n\nHave a great day!\n" + this.state.userSettings.company_name,
        });
    }

    // No QR invoices left this period (any plan): offer the bigger plans instead of a wizard that can't generate.
    newQrInvoice() {
        if (this.state.userSettings.remaining_qr_invoices_this_month !== undefined && this.state.userSettings.remaining_qr_invoices_this_month <= 0) {
            this.setState({premiumQuota: true});
            return;
        }
        this.props.history.push('/new-qr');
    }

    // The company upgraded from the out-of-invoices modal: on to the wizard, which reads the new allowance.
    createAfterUpgrade(plan) {
        this.setState({premiumQuota: false});
        toast.success("You're on " + ((plan && plan.plan_name) || "your new plan") + "! You can generate QR invoices again.");
        this.props.history.push('/new-qr');
    }

    // The company just bought a plan from the premium-feature modal: reload its settings (now paid) and carry on to the
    // email form for the invoice they wanted to send.
    emailAfterUpgrade(plan) {
        const invoice = this.state.premiumEmailInvoice;
        axios.get(window.SERVER_URL + '/get_settings').then((res) => {
            this.setState({userSettings: res.data.result, premiumEmailInvoice: null}, () => {
                toast.success("You're on " + ((plan && plan.plan_name) || "your new plan") + "! You can now email QR invoices.");
                if (invoice) {
                    this.openSendForm(invoice.id, invoice.title);
                }
            });
        }).catch(() => {
            this.setState({premiumEmailInvoice: null});
            showGenericErrorToast();
        });
    }

    sendEmail() {
        let _this = this;
        this.setState({sendInvoiceButtonLoading: true, messageRecipientIsInvalid: false, messageSubjectIsInvalid: false});
        const messageRecipient = this.state.messageRecipient.trim();
        if (messageRecipient.length < 1 || isValidEmailAddress(messageRecipient) === false) {
            this.setState({messageRecipientIsInvalid: true, sendInvoiceButtonLoading: false});
            return;
        }

        if (this.state.messageSubject.length < 1) {
            this.setState({messageSubjectIsInvalid: true, sendInvoiceButtonLoading: false});
            return;
        }

        axios.post(window.SERVER_URL + '/send_user_qr_invoice_as_email', {
            qr_invoice_id: this.state.selectedInvoiceToSendId,
            message_recipient: messageRecipient,
            message_subject: this.state.messageSubject,
            message_content: this.state.messageContent,
        }).then(function (response) {
            if (response.status === 200) {
                toast.success("Invoice sent successfully");
                _this.setState({sendInvoiceButtonLoading: false, messageRecipient: '', messageSubject: '', messageContent: '', sendQrInvoiceModalVisibility: false});
            }
        }).catch((err) => {
            // Re-enable the button, or it would stay disabled with its spinner after a failed send.
            _this.setState({sendInvoiceButtonLoading: false});
            if (isPremiumRequired(err)) {
                // The plan changed to Free since this page loaded: offer the plans rather than an error.
                _this.setState({sendQrInvoiceModalVisibility: false,
                                premiumEmailInvoice: {id: _this.state.selectedInvoiceToSendId, title: _this.state.selectedInvoiceToSendTitle}});
                return;
            }
            toast.error(isFrozenError(err) ? FROZEN_MESSAGE : emailSendErrorMessage(err));
        });
    }

    render() {
        if (this.state.isLoading) {
            return <LoadingScreen />;
        }

        // Only decide once /get_settings has answered (userSettings starts as {}), so the banner never flashes.
        const settingsLoaded = this.state.userSettings.has_quickbooks_company !== undefined;
        const quickBooksDisconnected = settingsLoaded && !isQuickBooksConnected(this.state.userSettings);
        // A failed payment freezes the company (is_active false): no new QR invoices and no invoice actions.
        const subscriptionFrozen = settingsLoaded && this.state.userSettings.is_active === false;
        // Why the New QR button and the row actions are blocked (null = they're available).
        const blockedReason = quickBooksDisconnected ? "QuickBooks is not connected"
            : subscriptionFrozen ? "Your subscription is frozen" : null;

        return (
            <div>
                {this.state.premiumQuota ? (
                    <PremiumFeatureModal userSettings={this.state.userSettings} title={QUOTA_PREMIUM_TITLE} upgradeOnly
                                         message={this.state.userSettings.has_active_paid_subscription ? QUOTA_PREMIUM_MESSAGE_PAID : QUOTA_PREMIUM_MESSAGE_FREE}
                                         onClose={() => this.setState({premiumQuota: false})}
                                         onUpgraded={(plan) => this.createAfterUpgrade(plan)} />
                ) : null}
                {this.state.premiumEmailInvoice ? (
                    <PremiumFeatureModal userSettings={this.state.userSettings} title={EMAIL_PREMIUM_TITLE} message={EMAIL_PREMIUM_MESSAGE}
                                         onClose={() => this.setState({premiumEmailInvoice: null})}
                                         onUpgraded={(plan) => this.emailAfterUpgrade(plan)} />
                ) : null}
                <SimpleModal wide show={this.state.sendQrInvoiceModalVisibility} onHide={() => this.setState({sendQrInvoiceModalVisibility: false})}
                             title={"Send QR Invoice: " + this.state.selectedInvoiceToSendTitle}
                             footer={<button className="btn btn-primary" disabled={this.state.sendInvoiceButtonLoading} onClick={this.sendEmail}>
                                         {this.state.sendInvoiceButtonLoading ? (
                                             <span style={{display: "inline-flex", alignItems: "center", gap: "8px"}}>
                                                 <span className="btn-spinner" /> Sending…
                                             </span>
                                         ) : "Send Invoice"}
                                     </button>}>
                    <div className="app-grid-row">
                        <div className="app-grid-col">
                            <div className="app-form-group">
                                <label className="app-form-label">To:</label>
                                <input className={"app-form-input" + (this.state.messageRecipientIsInvalid ? " invalid" : "")} name="messageRecipient" maxLength="200"
                                       placeholder="Enter message recipient here" onChange={this.handleChange} value={this.state.messageRecipient} />
                                {this.state.messageRecipientIsInvalid ? <span className="app-form-error">Invalid recipient address</span> : null}
                            </div>
                            <div className="app-form-group">
                                <label className="app-form-label">Subject:</label>
                                <input className={"app-form-input" + (this.state.messageSubjectIsInvalid ? " invalid" : "")} name="messageSubject" maxLength="200"
                                       value={this.state.messageSubject} placeholder="Enter message subject here" onChange={this.handleChange} />
                                {this.state.messageSubjectIsInvalid ? <span className="app-form-error">The message subject cannot be empty</span> : null}
                            </div>
                            <div className="app-form-group">
                                <label className="app-form-label">Message (optional):</label>
                                <textarea className="app-form-textarea" maxLength={2000} name="messageContent" rows="10"
                                          placeholder="Enter message body here (optional)" onChange={this.handleChange} value={this.state.messageContent} />
                                <span style={{color: "#8892a0", fontSize: "0.8rem"}}>*The Invoice will be sent as an attachment</span>
                            </div>
                        </div>
                        <div className="app-grid-col">
                            <Document file={this.state.selectedInvoiceToSendUrl} options={SAFE_PDF_OPTIONS} loading={<PdfLoadingSpinner />}>
                                <PDFPage pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} />
                            </Document>
                        </div>
                    </div>
                </SimpleModal>

                <SimpleModal show={this.state.viewQrInvoiceModalVisibility} onHide={() => this.setState({viewQrInvoiceModalVisibility: false})}
                             title={"Viewing Generated QR Invoice: " + this.state.selectedInvoiceToViewTitle}>
                    <div style={{textAlign: "center"}}>
                        <Document file={this.state.selectedInvoiceToViewUrl} options={SAFE_PDF_OPTIONS} loading={<PdfLoadingSpinner />}>
                            <PDFPage pageNumber={1} renderTextLayer={false} renderAnnotationLayer={false} />
                        </Document>
                    </div>
                </SimpleModal>

                <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "4px"}}>
                    <h2 style={{margin: 0}}>All QR Invoices</h2>
                    <button className="btn btn-primary" disabled={blockedReason !== null}
                            title={blockedReason || undefined}
                            onClick={() => this.newQrInvoice()}>+ New QR Invoice</button>
                </div>
                <div style={{color: "#8892a0", marginBottom: "24px"}}>View every QR invoice your company has generated.</div>

                {quickBooksDisconnected ? (
                    <div className="app-error-banner">
                        <span>⚠ QuickBooks is not connected for this company. Connect QuickBooks to create new QR invoices.</span>
                        <a href={window.SERVER_URL + '/oauth/init'} style={{display: "inline-flex"}}>
                            <img src={qb_connect_button} alt="Connect to QuickBooks" style={{display: "block"}} />
                        </a>
                    </div>
                ) : null}

                {subscriptionFrozen ? <FrozenSubscriptionBanner userSettings={this.state.userSettings} /> : null}

                <div className="app-card">
                    <h3 className="app-card-title">All QR Invoices</h3>

                    {(this.state.userSettings.plan_id === 1 && this.state.userSettings.remaining_qr_invoices_this_month <= 0) ? (
                        <div style={{background: "#fdf3e3", border: "1px solid #f3d9a4", color: "#9a6a1c", borderRadius: "8px", padding: "12px 16px", marginBottom: "16px", fontSize: "0.9rem"}}>
                            ⚠ You have reached the limit for Free plan. <a href="javascript:void(0)" onClick={() => this.props.history.push('/subscriptions')}>Upgrade</a> to create more QR Invoices
                        </div>
                    ) : null}

                    <div className="app-form-group">
                        <input className="app-form-input" placeholder="Search by invoice name or number..."
                               value={this.state.searchQuery} onChange={(e) => this.setState({searchQuery: e.target.value})} />
                    </div>

                    <div className="app-table-scroll">
                    <table className="app-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Created By</th>
                                <th>Creation Date</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {this.state.invoices.filter((qrInvoice) =>
                                qrInvoice.invoice_title.toLowerCase().includes(this.state.searchQuery.toLowerCase())
                            ).map((qrInvoice) => (
                                <tr key={qrInvoice.id}>
                                    <td>
                                        <div style={{display: "flex", alignItems: "center", gap: "12px"}}>
                                            <span style={{width: "32px", height: "32px", borderRadius: "8px", background: "#dce7d6", color: "#224514", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0}}>
                                                <GridIcon size={16} />
                                            </span>
                                            <div>
                                                <div style={{fontWeight: 700}}>{qrInvoice.invoice_title}</div>
                                                {qrInvoice.qr_type ? <div style={{color: "#8892a0", fontSize: "0.8rem"}}>{QR_TYPE_LABELS[qrInvoice.qr_type] || ''}</div> : null}
                                            </div>
                                        </div>
                                    </td>
                                    <td>{qrInvoice.generated_by_name}</td>
                                    <td>{new Date(qrInvoice.creation_date).toLocaleString()}</td>
                                    <td>
                                        {this.state.nowDownloadingQrInvoiceId === qrInvoice.id ? (
                                            <img style={{width: "16px", height: "16px"}} src={spinner} alt="" />
                                        ) : (
                                            <React.Fragment>
                                                {/* Disabled while QuickBooks is disconnected (every action fetches the PDF from it) or the subscription is frozen. */}
                                                {blockedReason ? (
                                                    // Same ⚠ glyph and red as the error banners above the table.
                                                    <span title={blockedReason} role="img" aria-label={blockedReason}
                                                          style={{color: "#c92a2a", fontSize: "1rem", lineHeight: 1, verticalAlign: "middle", marginRight: "14px", cursor: "default"}}>
                                                        ⚠
                                                    </span>
                                                ) : null}
                                                <button className="app-icon-btn" disabled={blockedReason !== null} title={blockedReason || "Download"} onClick={() => this.downloadInvoice(qrInvoice.id, qrInvoice.invoice_title)}><DownloadIcon size={16} /></button>
                                                <button className="app-icon-btn" disabled={blockedReason !== null} title={blockedReason || "View"} onClick={() => this.viewInvoice(qrInvoice.id, qrInvoice.invoice_title)}><EyeIcon size={16} /></button>
                                                <button className="app-icon-btn" disabled={blockedReason !== null} title={blockedReason || "Send"} onClick={() => this.sendInvoice(qrInvoice.id, qrInvoice.invoice_title)}><SendIcon size={16} /></button>
                                            </React.Fragment>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    </div>
                    {(this.state.invoices.length === 0) ?
                        <div style={{textAlign: "center", padding: '20px 0', color: "#8892a0"}}>No QR Invoices yet. Click "New QR Invoice" and create one.</div> : null}
                    {this.state.invoices.length > 0 ? (
                        <div style={{paddingTop: "16px", color: "#8892a0", fontSize: "0.85rem"}}>
                            Showing page {this.state.currentPage} of {this.state.totalPages} (total {this.state.totalEntries} entries)<br/>
                            {this.state.currentPage > 1 ? <a href="javascript:void(0)" onClick={() => this.previousPage()}>← Previous Page</a> : null}
                            {this.state.currentPage > 1 && this.state.currentPage < this.state.totalPages ? " | " : null}
                            {this.state.currentPage < this.state.totalPages ? <a href="javascript:void(0)" onClick={() => this.nextPage()}>Next Page →</a> : null}
                        </div>
                    ) : null}
                </div>
            </div>
        );
    }
}

export default AllInvoices;
